import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { API_BASE_URL } from "../utils/constants";
import { Pagination } from "react-bootstrap";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMinDate } from "../utils/hearingDateUtils";
import "./AssignSecondHearingDate.css";

const AssignThirdHearingDate = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);
  const [lookupDate, setLookupDate] = useState(null); // stores the 2nd hearing date used for lookup
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [thirdDate, setThirdDate] = useState(false);
  const [dateError, setDateError] = useState("");

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormattedDate(date ? date.toLocaleDateString("en-US") : "");
    setDateError("");
  };

  const handleAppointments = async () => {
    if (!selectedDate) {
      setDateError("Please select the second hearing date.");
      return;
    }

    const arbId = localStorage.getItem("Arb_id");
    if (!arbId) {
      alert("Arbitrator ID not found. Please login again.");
      return;
    }

    const dataAppointments = {
      Arb_id: parseInt(arbId),
      Meeting_No: 2,
      Hearing_date: formattedDate,
    };

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/GetOppforNextHearing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataAppointments),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch data: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const parsedData = Array.isArray(result) ? result : (result.data || []);
      const updatedData = parsedData.map((item) => {
        const { caseId, referenceNo, Cust_name, ...rest } = item;
        return { Case_id: caseId, Reference_No: referenceNo, Cust_name, ...rest };
      });

      setData(updatedData);
      setTotalPages(Math.ceil(updatedData.length / itemsPerPage));
      setLookupDate(selectedDate); // save 2nd hearing date as the baseline
      setSelectedDate(null);       // reset so user picks a fresh 3rd hearing date
      setFormattedDate(null);
      setThirdDate(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleThirdHearing = async () => {
    if (!selectedDate) {
      setDateError("Please select the third hearing date.");
      return;
    }
    if (selectedRows.length === 0) {
      alert("Please select at least one case.");
      return;
    }

    const formattedThirdDate = selectedDate.toLocaleDateString("en-US");
    const payload = selectedRows.map((row) => ({
      Case_id: row.Case_id || row.caseId,
      Meeting_No: 2,
      Second_Hearing_date: formattedThirdDate,
      Hearing_time_From: "",
      Hearing_time_To: "",
      Video_link: "",
      Link_id: "",
      Password: "",
      Comment: "",
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/AssignThird_date`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(err.message || `HTTP ${response.status}`);
      }

      toast.success("Third hearing dates successfully assigned", { theme: "colored" });
      setSelectedDate(null);
      setSelectedRows([]);
      setSelectAll(false);
      setData([]);
      setThirdDate(false);
      setLookupDate(null);
    } catch (error) {
      console.error("Error setting third hearing date:", error);
      toast.error(error.message, { theme: "colored" });
    }
  };

  const headers = data.length > 0 ? Object.keys(data[0]) : [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedRows(!selectAll ? data : []);
  };

  const handleRowSelection = (row) => {
    const isSelected = selectedRows.includes(row);
    setSelectedRows((prev) =>
      isSelected ? prev.filter((r) => r !== row) : [...prev, row]
    );
  };

  useEffect(() => {
    setTotalPages(Math.ceil(data.length / itemsPerPage));
  }, [data, itemsPerPage]);

  const generatePaginationItems = () => {
    const pageItems = [];
    const maxPaginationButtons = 5;
    let startPage = Math.max(currentPage - Math.floor(maxPaginationButtons / 2), 1);
    let endPage = Math.min(startPage + maxPaginationButtons - 1, totalPages);
    if (endPage - startPage + 1 < maxPaginationButtons) {
      startPage = Math.max(endPage - maxPaginationButtons + 1, 1);
    }
    if (startPage > 1) {
      pageItems.push(<Pagination.Item key="start" onClick={() => setCurrentPage(1)}>1</Pagination.Item>);
      if (startPage > 2) pageItems.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageItems.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageItems.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      pageItems.push(<Pagination.Item key="end" onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>);
    }
    return pageItems;
  };

  if (loading) return <LoadingSpinner />;

  const minThirdDate = getMinDate(lookupDate);

  return (
    <div className="container">
      <div className="row my-2">
        <div className="col-md-5">
          <h4>Assign Third Hearing Date</h4>
        </div>
      </div>

      <div className="row d-flex align-items-center">
        {!thirdDate && (
          <>
            <div className="col-md-3">
              <label htmlFor="datePicker">Select Second Hearing Date</label>
            </div>
            <div className="col-md-3">
              <DatePicker
                id="datePicker"
                selected={selectedDate}
                onChange={handleDateChange}
                placeholderText="Select a date"
                dateFormat="MM/dd/yyyy"
                className={`form-control date-picker-sm ${dateError ? "is-invalid" : ""}`}
              />
              {dateError && (
                <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "3px" }}>
                  {dateError}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <button className="custBtn" onClick={handleAppointments}>
                Get Data
              </button>
            </div>
          </>
        )}

        {thirdDate && (
          <>
            <div className="col-md-3">
              <label htmlFor="datePicker2">
                Select Third Hearing Date
                <span style={{ fontSize: "0.75rem", color: "#888", marginLeft: "6px" }}>
                  (must be after {lookupDate?.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })})
                </span>
              </label>
            </div>
            <div className="col-md-3">
              <DatePicker
                id="datePicker2"
                selected={selectedDate}
                onChange={handleDateChange}
                placeholderText="Select a date"
                dateFormat="MM/dd/yyyy"
                className={`form-control date-picker-sm ${dateError ? "is-invalid" : ""}`}
                minDate={minThirdDate}
              />
              {dateError && (
                <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "3px" }}>
                  {dateError}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <button className="custBtn" onClick={handleThirdHearing}>
                Set Date
              </button>
            </div>
          </>
        )}
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive">
            {data.length > 0 && (
              <table className="table table-striped table-bordered table-hover mt-3 text-center no-wrap-table">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    </th>
                    {headers.map((header) => <th key={header}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index} className="text-center custom_fz">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(item)}
                          onChange={() => handleRowSelection(item)}
                        />
                      </td>
                      {headers.map((header) => <td key={header}>{item[header]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="justify-content-center custom-pagination">
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))}
                disabled={currentPage === 1}
              />
              {generatePaginationItems()}
              <Pagination.Next
                onClick={() => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev))}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AssignThirdHearingDate;
